import { Space, Row, Col, Button, AutoComplete } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { UserType } from '../../types/types';
import { useContext, useState, useEffect } from 'react';
import { updateMe } from '../../features/user/userSlice';

import { NotificationContext } from '../../providers/NotificationProvider';
import { constants } from '../../constants/constants';

import paises from '../../constants/paises.json';
import { useTranslation } from 'react-i18next';

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { triggerNotification } = useContext(NotificationContext);
  const { user, rol } = useSelector((store: any) => store.user);
  const [editMode, setEditMode] = useState(false);
  const [options, setOptions] = useState<{ value: string; phoneCode: string }[]>([]);

  const onSearch = (searchText: string) => {
    const filteredOptions = paises
      .filter(
        (pais) => pais.nameES.toLowerCase().includes(searchText.toLowerCase()) || pais.phoneCode.includes(searchText)
      )
      .map((pais) => ({
        value: `${pais.nameES} (+${pais.phoneCode})`,
        phoneCode: pais.phoneCode,
      }));
    setOptions(filteredOptions);
  };

  const onSelect = (value: string) => {
    const selectedCode = options.find((option) => option.value === value)?.phoneCode || '';
    setCurrentUser({ ...currentUser, callcode: selectedCode });
  };

  const [currentUser, setCurrentUser] = useState<UserType>({
    id: user?.id,
    company_name: user?.company_name,
    first_name: user?.first_name,
    last_name: user?.last_name,
    username: user?.username,
    email: user?.email,
    is_staff: user?.is_staff,
    is_active: user?.is_active,
    date_joined: user?.date_joined,
    company: user?.company,
    callcode: user?.callcode,
    groups: user?.groups,
    position: user?.position,
    phone: user?.phone,
    employer_name: user?.employer_name,
  });

  useEffect(() => {
    setCurrentUser({
      id: user?.id,
      company_name: user?.company_name,
      first_name: user?.first_name,
      last_name: user?.last_name,
      username: user?.username,
      email: user?.email,
      is_staff: user?.is_staff,
      is_active: user?.is_active,
      date_joined: user?.date_joined,
      company: user?.company,
      callcode: user?.callcode,
      groups: user?.groups,
      position: user?.position,
      phone: user?.phone,
      employer_name: user?.employer_name,
    } as UserType);
  }, [user]);

  const saveMyData = () => {
    dispatch(updateMe(currentUser as UserType) as any)
      .then((result: any) => {
        if (updateMe.fulfilled.match(result)) {
          setEditMode(false);
          triggerNotification('success', t('Actualización Exitosa'), t('Tus datos han sido actualizados.'), 'bottomRight');
        } else {
          triggerNotification('error', t('Error'), result.message, 'bottomRight');
        }
      })
      .catch((error: any) => {
        triggerNotification('error', t('Error'), error.message, 'bottomRight');
      });
  };

  return (
    <Row className='content'>
      <Col sm={12}>
        <h1>{t('Perfil de Usuario')}</h1>
        <Space />
        {!editMode && (
          <div className='user-data'>
            {rol === constants.ROL_EMPRESA ? (
              <p>
                <b>{t('Nombre de Empresa:')}</b> {user?.company_name}
              </p>
            ) : (
              <p>
                <b>{t('Nombre Completo:')}</b> {user?.first_name} {user?.last_name}
              </p>
            )}

            <p>
              <b>{t('Nombre de Usuario:')}</b> {user?.username}
            </p>
            <p>
              <b>{t('Email:')}</b> {user?.email}
            </p>
            <p>
              <b>{t('Rol:')}</b> {rol}
            </p>
            <p>
              <b>{t('Teléfono:')}</b>+{user?.callcode} {user?.phone}
            </p>
            {rol === constants.ROL_USER && (
              <>
                <p>
                  <b>{t('Empresa:')} </b> {user?.company?.name}
                  <br />
                  <b>{t('Cargo:')} </b> {user?.position}
                </p>
              </>
            )}
            <Button type='primary' onClick={() => setEditMode(true)}>
              {t('Editar')}
            </Button>
          </div>
        )}
        {editMode && (
          <div className='edit-user-data'>
            {rol === constants.ROL_EMPRESA ? (
              <label>
                <b>{t('Nombre de Empresa:')}</b>
                <input
                  type='text'
                  placeholder={t('Nombres')}
                  value={currentUser?.company_name}
                  onChange={(e: any) => setCurrentUser({ ...currentUser, company_name: e.target.value })}
                />
              </label>
            ) : (
              <>
                <label>
                  <b>{t('Nombres:')}</b>
                  <input
                    type='text'
                    placeholder={t('Nombres')}
                    value={currentUser?.first_name}
                    onChange={(e: any) => setCurrentUser({ ...currentUser, first_name: e.target.value })}
                  />
                </label>
                <label>
                  <b>{t('Apellidos:')}</b>
                  <input
                    type='text'
                    placeholder={t('Apellidos')}
                    value={currentUser?.last_name}
                    onChange={(e: any) => setCurrentUser({ ...currentUser, last_name: e.target.value })}
                  />
                </label>
              </>
            )}
            <label>
              <b>{t('Nombre de usuario:')}</b>
              <input
                type='text'
                placeholder={t('Nombre de Usuario')}
                value={currentUser?.username}
                onChange={(e: any) => setCurrentUser({ ...currentUser, username: e.target.value })}
              />
            </label>

            <label>
              <b>{t('Correo Electrónico:')}</b>
              <input
                type='email'
                placeholder={t('Email')}
                value={currentUser?.email}
                onChange={(e: any) => setCurrentUser({ ...currentUser, email: e.target.value })}
              />
            </label>
            <label>
              <b>{t('Teléfono:')}</b>
              <Row>
                <Col sm={10}>
                  <AutoComplete
                    options={options.map((option) => ({ value: option.value }))}
                    onSearch={onSearch}
                    onSelect={onSelect}
                    placeholder={t('Buscar país o código')}
                    filterOption={(inputValue, option) =>
                      option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                    style={{ width: '95%', height: 41 }}
                    value={currentUser?.callcode}
                  />
                </Col>
                <Col sm={14}>
                  <input
                    type='text'
                    placeholder={t('Teléfono')}
                    value={currentUser.phone ?? ''}
                    onChange={(e: any) => setCurrentUser({ ...currentUser, phone: e.target.value })}
                  />
                </Col>
              </Row>
            </label>
            <br />
            <Button type='primary' onClick={saveMyData}>
              {t('Guardar Cambios')}
            </Button>
            <br />
            <Button
              type='default'
              onClick={() => {
                setEditMode(false);
              }}
            >
              {t('Cancelar')}
            </Button>
          </div>
        )}
      </Col>
    </Row>
  );
};

export default Profile;
